'use client';

// Opted out of React Compiler with the grid it serves: columns keep their identity when pinning,
// order or visibility change, so compiled reads of them go stale (see `table.tsx`).
'use no memo';

// Header drag: a grip on each movable head lifts its whole column, header and rendered cells, and
// drops it into a slot among the unpinned columns or into and within either pinned section
// (`header-drop-slots.ts`). While it drags, the other columns slide aside to open a gap where it
// will land, and the sections show over the table, the one it would land in saying what the drop
// does. A pointer pins the column only after resting on a pinned section, so scrolling the
// unpinned columns from the table's edge doesn't pin it by accident. On drop the lifted column
// settles into the gap before the layout changes, so nothing jumps. Pointer drags run on dnd-kit's sensors and auto-scroll; the keyboard steps through the
// slots itself, as the edge between two sections holds two slots at one x.
//
// The slide is one injected stylesheet rule per moved column, keyed by `data-column-id`, so rows
// a virtual table mounts mid-drag slide too, and no React render runs while the pointer moves.
import { createContext, type Dispatch, type KeyboardEvent, type ReactNode, type RefObject, type SetStateAction, useContext, useEffect, useId, useState } from 'react';
import { createPortal, flushSync } from 'react-dom';
import { type Announcements, DndContext, MouseSensor, TouchSensor, useDraggable, useSensor, useSensors } from '@repo/ui/dnd';
import { GripVerticalIcon, PinIcon } from '@repo/ui/icons';
import { cn } from '@repo/ui/lib/utils';
import { getColumnHeaderLabel } from '../data-grid/data-grid';
import { moveTableColumnLayout, sameLayout, type TableColumnRegion } from '../table/context/table-layout';
import { useTableState } from '../table/context/table-state-provider';
import { getTableSettingsMoveAnnouncement, getTableSettingsNoMoveAnnouncement, getTableSettingsPosition, TABLE_COLUMN_REGION_LABELS, TABLE_SETTINGS_DND_ACTIVATION } from '../table/settings-dnd';
import { getStickySides } from './sticky-columns';
import type { Column, RowData, Table, TableColumnPinningState } from '../table/tanstack-table';
import { getHeaderDragOffsets, getHeaderDropMove, getHeaderDropSlotAt, getHeaderDropSlots, getHeaderHomeSlot, HEADER_DROP_EMPTY_BAND, HEADER_DROP_REGIONS, type HeaderDragGeometry, type HeaderDropLocks, type HeaderDropRegions, type HeaderDropSlot, isSameHeaderDropSlot, stepHeaderDropSlot } from './header-drop-slots';
type HeaderDrag = {
  onGripBlur: (columnId: string) => void;
  onGripKeyDown: (columnId: string, event: KeyboardEvent<HTMLButtonElement>) => void;
  onGripPointerDown: (x: number) => void;
  registerCell: (columnId: string, element: HTMLTableCellElement | null) => void;
  registerGrip: (columnId: string, element: HTMLButtonElement | null) => void;
};

/** A box from the window's top left. */
type HeaderDragBox = {
  height: number;
  left: number;
  top: number;
  width: number;
};

/** What the drag reads when it runs, handed over by the provider after each render. */
type HeaderDragDeps = {
  columnOrder: string[];
  columnPinning: TableColumnPinningState;
  columnVisibility: Record<string, boolean>;
  getViewport: () => HTMLElement | null;
  isDraggingRef: RefObject<boolean>;
  scope: string;
  setAnnouncement: Dispatch<SetStateAction<{
    message: string;
    sequence: number;
  }>>;
  setColumnOrder: (order: string[]) => void;
  setColumnPinning: (pinning: TableColumnPinningState) => void;
  setSections: Dispatch<SetStateAction<HeaderDragSections | null>>;
  stickyViewportWidth: null | number;
  table: Table<RowData, object>;
  tableRef: RefObject<HTMLTableElement | null>;
};

/** What the sections overlay shows: where the viewport is, how wide each pinned section is. */
type HeaderDragSections = {
  /** What a drop in the target section does, in its label. */
  action: string;
  endWidth: number;
  /** The pinned section a hold is filling, before it takes the drop. */
  hold: null | TableColumnRegion;
  rect: HeaderDragBox;
  region: TableColumnRegion;
  startWidth: number;
};
type HeaderDragSession = {
  activeId: string;
  /** The pinned section a filled hold opened to the drop, until the pointer leaves it. */
  armed: null | TableColumnRegion;
  /** The viewport's content box, measured at the start and on every scroll. */
  box: HeaderDragBox | null;
  geometry: HeaderDragGeometry;
  /** The middle of the dragged column's head, where the heads along its row are hit-tested. */
  headY: number;
  hold: null | {
    region: TableColumnRegion;
    timer: number;
  };
  /** Where the lifted column started, from the window's left. */
  liftLeft: number;
  locks: HeaderDropLocks;
  regions: HeaderDropRegions;
  slot: HeaderDropSlot;
  slots: HeaderDropSlot[];
  via: 'keyboard' | 'pointer';
};
const HeaderDragContext = createContext<HeaderDrag | null>(null);

// dnd-kit announces nothing: the drag announces slots and drops in table settings' words itself.
const silent = () => undefined;
const ANNOUNCEMENTS: Announcements = {
  onDragCancel: silent,
  onDragEnd: silent,
  onDragMove: silent,
  onDragOver: silent,
  onDragStart: silent
};
// How long the pointer rests on a pinned section before a drop there pins the column.
const HOLD_TO_PIN_MS = 600;
const SCREEN_READER_INSTRUCTIONS = {
  draggable: 'To move this column, press Space or Enter, then the arrow keys, which also move it into or out of the pinned columns. Press Space or Enter to drop it, or Escape to cancel.'
};

// Auto-scroll runs sideways only. It reads the raw pointer movement, not a locked axis, and the
// header sits in the top band where it would scroll a scrolled-down table up. A `y` threshold of
// 0 divides by zero in dnd-kit 6.3.1; negative infinity makes both vertical edge checks false.
// Layout-shift compensation is off: the dragged node is the grip, which doesn't move at drag start.
const AUTO_SCROLL = {
  layoutShiftCompensation: false,
  threshold: {
    x: 0.2,
    y: Number.NEGATIVE_INFINITY
  }
} as const;

// The slide and the settle share one curve and length, so the lifted column lands as the gap
// finishes opening.
const MOTION_MS = 200;
const MOTION_EASE = 'cubic-bezier(0.2, 0.8, 0.2, 1)';
const LIFTED_SHADOW = '0 18px 38px -8px rgb(0 0 0 / 0.32), 0 4px 10px rgb(0 0 0 / 0.12)';
const regionLabel = (region: TableColumnRegion) => TABLE_COLUMN_REGION_LABELS[region];
const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Runs header drag for the grid inside it. `isDraggingRef` tells the grid's own Escape handler to
 * leave Escape to the drag, `getViewport` gives the element that scrolls the table, and
 * `stickyViewportWidth` is the width the grid's sticky budget applies to, `null` off phones.
 */
export const HeaderDragProvider = ({
  children,
  getViewport,
  isDraggingRef,
  stickyViewportWidth
}: {
  children: ReactNode;
  getViewport: () => HTMLElement | null;
  isDraggingRef: RefObject<boolean>;
  stickyViewportWidth: null | number;
}) => {
  const {
    columnOrder,
    columnPinning,
    columnVisibility,
    setColumnOrder,
    setColumnPinning,
    table,
    tableRef
  } = useTableState(state => ({
    columnOrder: state.columnOrder,
    columnPinning: state.columnPinning,
    columnVisibility: state.columnVisibility
  }));
  const [announcement, setAnnouncement] = useState({
    message: '',
    sequence: 0
  });
  const [sections, setSections] = useState<HeaderDragSections | null>(null);
  // One object for the grid's life, so the heads' context never changes.
  const [drag] = useState(createHeaderDrag);
  const scope = useId();
  const sensors = useSensors(useSensor(MouseSensor, {
    activationConstraint: TABLE_SETTINGS_DND_ACTIVATION.pointer
  }), useSensor(TouchSensor, {
    activationConstraint: TABLE_SETTINGS_DND_ACTIVATION.touch
  }));

  // A grid that unmounts mid-drag leaves nothing behind.
  useEffect(() => () => drag.dispose(), [drag]);
  useEffect(() => {
    drag.connect({
      columnOrder,
      columnPinning,
      columnVisibility,
      getViewport,
      isDraggingRef,
      scope,
      setAnnouncement,
      setColumnOrder,
      setColumnPinning,
      setSections,
      stickyViewportWidth,
      table,
      tableRef
    });
  });
  return <HeaderDragContext.Provider value={drag} data-sentry-element="HeaderDragContext.Provider" data-sentry-component="HeaderDragProvider" data-sentry-source-file="header-drag.tsx">
      <DndContext accessibility={{
      announcements: ANNOUNCEMENTS,
      screenReaderInstructions: SCREEN_READER_INSTRUCTIONS
    }} autoScroll={AUTO_SCROLL} id={useId()} onDragCancel={() => drag.endPointer(false)} onDragEnd={() => drag.endPointer(true)} onDragStart={event => drag.startPointer(String(event.active.id), event.activatorEvent instanceof MouseEvent ? event.activatorEvent.clientX : undefined)} sensors={sensors} data-sentry-element="DndContext" data-sentry-source-file="header-drag.tsx">
        {children}
      </DndContext>
      {sections && createPortal(<SectionsOverlay {...sections} />, document.body)}
      <p aria-live="polite" className="sr-only" role="status">
        <span key={announcement.sequence}>{announcement.message}</span>
      </p>
    </HeaderDragContext.Provider>;
};

// Diagonal stripes, as in the gap the dragged column leaves, so the pinned sections read as
// places of their own.
const pinnedStripes = (percent: number) => `repeating-linear-gradient(135deg, color-mix(in oklab, var(--primary) ${percent}%, transparent) 0 6px, transparent 6px 12px)`;
const SECTION_CLASS = 'absolute inset-y-0 transition-[left,width,background-color,box-shadow] duration-200 ease-out motion-reduce:transition-none';
const LABEL_CLASS = 'absolute -top-3.5 left-1/2 flex -translate-x-1/2 items-center gap-1 overflow-hidden whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold shadow-sm';
const HOLD_LABELS = {
  end: 'Hold to pin right',
  start: 'Hold to pin left'
} as const;

// How each section shows: the target, a pinned one a hold is filling, or one at rest.
type SectionTone = 'holding' | 'on' | 'pinned' | 'unpinned';
const sectionToneOf = (isOn: boolean, isPinned: boolean, isHolding: boolean): SectionTone => {
  if (isHolding) return 'holding';
  if (isOn) return 'on';
  return isPinned ? 'pinned' : 'unpinned';
};
const SECTION_TONE_CLASS = {
  holding: 'ring-1 ring-primary/40 ring-inset outline-2 -outline-offset-2 outline-primary/70 outline-dashed',
  on: 'ring-2 ring-primary ring-inset',
  pinned: 'ring-1 ring-primary/40 ring-inset',
  unpinned: 'ring-1 ring-border ring-inset'
} as const satisfies Record<SectionTone, string>;
const LABEL_TONE_CLASS = {
  holding: 'bg-background text-foreground ring-2 ring-primary',
  on: 'bg-primary text-primary-foreground ring-1 ring-primary',
  pinned: 'bg-background text-muted-foreground ring-1 ring-border',
  unpinned: 'bg-background text-muted-foreground ring-1 ring-border'
} as const satisfies Record<SectionTone, string>;
// A pinned section's fill and stripes, stronger as it becomes the target.
const PINNED_FILL = {
  holding: {
    background: 'var(--background) 30%',
    stripes: 16
  },
  on: {
    background: 'var(--primary) 14%',
    stripes: 22
  },
  pinned: {
    background: 'var(--background) 30%',
    stripes: 9
  }
} as const;

// The hold's fill crosses its label in the time the hold takes. Outside the overlay, so it runs
// once, as the label mounts.
const fillHold = (element: HTMLElement | null) => {
  if (!element || prefersReducedMotion()) return;
  element.animate([{
    transform: 'scaleX(0)'
  }, {
    transform: 'scaleX(1)'
  }], {
    duration: HOLD_TO_PIN_MS,
    fill: 'forwards'
  });
};

/**
 * The three sections over the table while a column drags: the target one lit, with its action,
 * and a pinned one filling while a hold runs on it.
 */
const SectionsOverlay = ({
  action,
  endWidth,
  hold,
  rect,
  region,
  startWidth
}: HeaderDragSections) => {
  const start = startWidth || HEADER_DROP_EMPTY_BAND;
  // The unpinned section keeps a band to drop on even when every other column is pinned.
  const end = Math.min(endWidth || HEADER_DROP_EMPTY_BAND, rect.width - start - HEADER_DROP_EMPTY_BAND);
  const section = (target: TableColumnRegion, left: number, width: number) => {
    const isOn = region === target;
    const side = target === 'center' ? null : target;
    const isHolding = side !== null && hold === side;
    const tone = sectionToneOf(isOn, side !== null, isHolding);
    const fill = tone === 'unpinned' ? null : PINNED_FILL[tone];
    let label: string = regionLabel(target);
    if (isHolding) label = HOLD_LABELS[side];else if (isOn) label = action;
    return <div className={cn(SECTION_CLASS, SECTION_TONE_CLASS[tone])} data-active={isOn || undefined} data-header-drag-section={target} style={{
      left,
      width: Math.max(width, 0),
      ...(fill && {
        backgroundColor: `color-mix(in oklab, ${fill.background}, transparent)`,
        backgroundImage: pinnedStripes(fill.stripes)
      })
    }} data-sentry-component="section" data-sentry-source-file="header-drag.tsx">
        {/* The edge the unpinned columns scroll under. */}
        {side && <span className={cn('absolute inset-y-0 w-0.5 bg-primary/60', side === 'start' ? '-right-px' : '-left-px')} />}
        <span className={cn(LABEL_CLASS, LABEL_TONE_CLASS[tone])}>
          {isHolding && <span className="absolute inset-0 origin-left bg-primary/25" ref={fillHold} />}
          {side && <PinIcon aria-hidden="true" className="relative size-3" />}
          <span className="relative">{label}</span>
        </span>
      </div>;
  };
  return <div aria-hidden="true" className="pointer-events-none fixed z-50" style={{
    height: rect.height,
    left: rect.left,
    top: rect.top,
    width: rect.width
  }} data-sentry-component="SectionsOverlay" data-sentry-source-file="header-drag.tsx">
      {/* The pinned sections last, so their edges show over the unpinned one's ring. */}
      {section('center', start, rect.width - start - end)}
      {section('start', 0, start)}
      {section('end', rect.width - end, end)}
    </div>;
};
function createHeaderDrag() {
  let deps: HeaderDragDeps | null = null;
  const connected = () => {
    if (!deps) throw new Error('Header drag ran before its provider rendered');
    return deps;
  };
  const cells = new Map<string, HTMLTableCellElement>();
  const grips = new Map<string, HTMLButtonElement>();
  let session: HeaderDragSession | null = null;
  let pressX = 0;
  let pointer: null | {
    x: number;
    y: number;
  } = null;
  let lift: HTMLElement | null = null;
  let sheet: HTMLStyleElement | null = null;
  let scrollFrame = 0;
  let settleTimer = 0;
  // Lands a drop still settling, when the next drag starts before it has.
  let flushSettle: (() => void) | null = null;
  const announce = (message: string) => connected().setAnnouncement(current => ({
    message,
    sequence: current.sequence + 1
  }));
  const getLabel = (columnId: string) => {
    const column = connected().table.getColumn(columnId);
    return column ? getColumnHeaderLabel(column) : columnId;
  };
  const describe = (active: HeaderDragSession, slot: HeaderDropSlot) => {
    const total = active.regions[slot.region].filter(id => id !== active.activeId).length + 1;
    return `${regionLabel(slot.region)}, position ${slot.index + 1} of ${total}`;
  };

  // What a drop in `slot` does, in the target section's label.
  const actionOf = (active: HeaderDragSession, slot: HeaderDropSlot) => {
    const home = getHeaderHomeSlot(active);
    if (isSameHeaderDropSlot(slot, home)) return regionLabel(slot.region);
    if (home.region === slot.region) return home.region === 'center' ? 'Move here' : 'Reorder pinned';
    if (slot.region === 'center') return 'Unpin';
    return slot.region === 'start' ? 'Pin left' : 'Pin right';
  };

  // The viewport's content box from the window's top left, down to the table's bottom.
  const viewportBox = (): HeaderDragBox | null => {
    const viewport = connected().getViewport();
    if (!viewport) return null;
    const rect = viewport.getBoundingClientRect();
    const tableBottom = connected().tableRef.current?.getBoundingClientRect().bottom ?? rect.bottom;
    const top = rect.top + viewport.clientTop;
    return {
      height: Math.max(0, Math.min(viewport.clientHeight, tableBottom - top)),
      left: rect.left + viewport.clientLeft,
      top,
      width: viewport.clientWidth
    };
  };

  // Where the dragged column's head shows, for hit-testing the heads along its row.
  const headMiddle = (activeId: string) => {
    const rect = cells.get(activeId)?.getBoundingClientRect();
    return rect ? rect.top + rect.height / 2 : 0;
  };

  // The layout as the drag starts. A layout change during the drag cancels it (`connect`).
  const freeze = (activeId: string): Omit<HeaderDragSession, 'armed' | 'hold' | 'liftLeft' | 'slot' | 'via'> => {
    const {
      getViewport,
      stickyViewportWidth,
      table
    } = connected();
    const ids = (columns: Column<RowData, unknown>[]) => columns.map(column => column.id);
    const regions: HeaderDropRegions = {
      center: ids(table.getCenterVisibleLeafColumns()),
      end: ids(table.getEndVisibleLeafColumns()),
      start: ids(table.getStartVisibleLeafColumns())
    };
    const leaves = table.getAllLeafColumns();
    const locked = (side: 'end' | 'start') => ids(leaves.filter(column => column.columnDef.meta?.lockedPinning === side));
    const locks = {
      end: locked('end'),
      start: locked('start')
    };
    const viewport = getViewport();
    // Measured, so the column taking the free width counts at the width it shows.
    const widths = new Map(HEADER_DROP_REGIONS.flatMap(region => regions[region]).map(id => [id, cells.get(id)?.getBoundingClientRect().width ?? table.getColumn(id)?.getSize() ?? 0]));
    // The grid's own sticky rule, run on each layout the drag shows: on a phone the pinned
    // columns past half the width scroll, and which those are depends on every pinned column.
    const lookup = {
      getColumn: (id: string) => {
        const width = widths.get(id);
        return width === undefined ? undefined : {
          getIsVisible: () => true,
          getSize: () => width
        };
      }
    };
    const stickyFor = (layout: HeaderDropRegions) => new Set(getStickySides(lookup, layout, {}, stickyViewportWidth).keys());
    return {
      activeId,
      box: viewportBox(),
      geometry: {
        scrollLeft: viewport?.scrollLeft ?? 0,
        stickyFor,
        viewportWidth: viewport?.clientWidth ?? 0,
        widths
      },
      headY: headMiddle(activeId),
      locks,
      regions,
      slots: getHeaderDropSlots({
        activeId,
        locks,
        regions
      })
    };
  };

  // The rules that slide every moved column, and paint the dragged one's cells as the gap.
  // `animate` is off when a scroll moves the columns, so sticky ones keep up with it.
  const writeSheet = (activeId: string, offsets: Map<string, number>, animate: boolean) => {
    const scope = CSS.escape(connected().scope);
    const table = `[data-header-drag="${scope}"]`;
    const cell = (id: string) => `${table} :is(th, td)[data-column-id="${CSS.escape(id)}"]`;
    const slides = [...offsets].map(([id, dx]) => `${cell(id)} { transform: translateX(${dx}px) !important; }`).join('\n');
    const gap = cell(activeId);
    if (!sheet) {
      sheet = document.createElement('style');
      document.head.append(sheet);
    }
    sheet.textContent = [animate ? `@media (prefers-reduced-motion: no-preference) { ${table} :is(th, td)[data-column-id] { transition: transform ${MOTION_MS}ms ${MOTION_EASE}; } }` : '', slides, `${gap} { background: repeating-linear-gradient(135deg, color-mix(in oklab, var(--primary) 8%, transparent) 0 6px, transparent 6px 12px) !important; box-shadow: inset 2px 0 0 color-mix(in oklab, var(--primary) 40%, transparent), inset -2px 0 0 color-mix(in oklab, var(--primary) 40%, transparent) !important; color: transparent !important; }`,
    // Transparent, not hidden, so a keyboard drag's grip keeps focus.
    `${gap} > * { opacity: 0 !important; }`].join('\n');
  };

  // Lays the table out with the column in `slot`: slides, gap and sections.
  let shownSections = '';
  const preview = (active: HeaderDragSession, slot: HeaderDropSlot, animate = true) => {
    active.slot = slot;
    const {
      endWidth,
      offsets,
      positions,
      startWidth
    } = getHeaderDragOffsets(active, slot, active.geometry);
    writeSheet(active.activeId, offsets, animate);
    const rect = active.box;
    if (rect) {
      const sections = {
        action: actionOf(active, slot),
        endWidth,
        hold: active.hold?.region ?? null,
        rect,
        region: slot.region,
        startWidth
      };
      // Rendered only when it changes, not on every frame of an auto-scroll.
      const key = JSON.stringify(sections);
      if (key !== shownSections) {
        shownSections = key;
        connected().setSections(sections);
      }
    }
    return {
      gapLeft: (rect?.left ?? 0) + (positions.get(active.activeId) ?? 0)
    };
  };
  const liftTransition = (animate: boolean) => prefersReducedMotion() ? 'none' : animate ? `box-shadow ${MOTION_MS}ms ${MOTION_EASE}, transform ${MOTION_MS}ms ${MOTION_EASE}` : `box-shadow ${MOTION_MS}ms ${MOTION_EASE}`;

  // A floating copy of the column's head and the cells in view, laid where the column shows.
  // Copied from the page, like the resize drag's live writes, as a virtual table renders only
  // some rows.
  const liftColumn = (active: HeaderDragSession) => {
    const head = cells.get(active.activeId);
    const table = connected().tableRef.current;
    const rect = active.box;
    if (!head || !table || !rect) return 0;
    const headRect = head.getBoundingClientRect();
    // Down to the viewport's bottom, or a sticky totals footer's top.
    const bottom = Math.min(rect.top + rect.height, table.tFoot?.getBoundingClientRect().top ?? Number.POSITIVE_INFINITY);
    const copy = (element: Element, height: number) => {
      const clone = element.cloneNode(true);
      if (!(clone instanceof HTMLElement)) return null;
      // A copy for the eye only: no ids or test ids to collide with the table's own.
      for (const node of [clone, ...clone.querySelectorAll('[id], [data-testid]')]) {
        node.removeAttribute('id');
        node.removeAttribute('data-testid');
      }
      // Out of the table's sticky layout, at the size the cell shows.
      clone.style.position = 'static';
      clone.style.insetInline = 'auto';
      clone.style.transform = 'none';
      clone.style.width = `${headRect.width}px`;
      clone.style.height = `${height}px`;
      return clone;
    };
    const shell = table.cloneNode(false);
    if (!(shell instanceof HTMLElement)) return 0;
    shell.removeAttribute('data-header-drag');
    shell.removeAttribute('id');
    shell.style.width = `${headRect.width}px`;
    shell.style.tableLayout = 'fixed';
    const thead = document.createElement('thead');
    const headRow = document.createElement('tr');
    const headCopy = copy(head, headRect.height);
    if (headCopy) {
      // Above the rows, which a transform lifts onto their own layer.
      headCopy.style.position = 'relative';
      headCopy.style.zIndex = '1';
      headRow.append(headCopy);
    }
    thead.append(headRow);
    const tbody = document.createElement('tbody');
    const selector = `td[data-column-id="${CSS.escape(active.activeId)}"]`;
    let firstRowTop: number | undefined;
    for (const row of table.tBodies[0]?.rows ?? []) {
      const rowRect = row.getBoundingClientRect();
      // Only the rows in view.
      if (rowRect.bottom <= headRect.bottom || rowRect.top >= bottom) continue;
      const cell = row.querySelector(selector);
      const rowCopy = row.cloneNode(false);
      const cellCopy = cell ? copy(cell, rowRect.height) : null;
      if (!(rowCopy instanceof HTMLElement) || !cellCopy) continue;
      firstRowTop ??= rowRect.top;
      rowCopy.removeAttribute('style');
      rowCopy.removeAttribute('data-testid');
      rowCopy.append(cellCopy);
      tbody.append(rowCopy);
    }
    // The rows sit where the table shows them, under the head.
    tbody.style.transform = `translateY(${(firstRowTop ?? headRect.bottom) - headRect.bottom}px)`;
    shell.append(thead, tbody);
    lift = document.createElement('div');
    lift.setAttribute('aria-hidden', 'true');
    lift.inert = true;
    lift.dataset.headerDragLift = active.activeId;
    lift.className = 'pointer-events-none fixed z-50 overflow-hidden rounded-md bg-background shadow-sm ring-1 ring-border';
    lift.style.left = `${headRect.left}px`;
    lift.style.top = `${headRect.top}px`;
    lift.style.width = `${headRect.width}px`;
    lift.style.height = `${Math.max(headRect.height, bottom - headRect.top)}px`;
    lift.style.transition = liftTransition(true);
    lift.append(shell);
    document.body.append(lift);
    // Lifted: raised a little, with a deeper shadow.
    requestAnimationFrame(() => {
      if (!lift) return;
      lift.style.boxShadow = LIFTED_SHADOW;
      lift.style.transform = 'translate3d(0, -4px, 0) scale(1.02)';
    });
    return headRect.left;
  };

  // Puts the lifted column's left edge at `x`, following the pointer or easing to a slot.
  const moveLift = (x: number, animate: boolean) => {
    if (!lift || !session) return;
    lift.style.transition = liftTransition(animate);
    lift.style.transform = `translate3d(${x - session.liftLeft}px, -4px, 0) scale(1.02)`;
  };

  // The slot under the pointer: the pinned sections' spans first, then the head under it. Every
  // layout read comes before the writes, so a move lays the page out once.
  const followPointer = () => {
    const active = session;
    if (!active || !pointer || !active.box) return;
    const hitId = document.elementFromPoint(pointer.x, active.headY)?.closest('th')?.dataset.columnId;
    // The gap is the dragged column's own head, which shows where it's going, not where it was.
    const hitRegion = hitId === undefined || hitId === active.activeId ? undefined : HEADER_DROP_REGIONS.find(region => active.regions[region].includes(hitId));
    const x = pointer.x - active.box.left;
    const hit = getHeaderDropSlotAt({
      ...active,
      geometry: active.geometry,
      hitRegion,
      x
    });
    const slot = holdToPin(active, hit, x);
    moveLift(active.liftLeft + pointer.x - pressX, false);
    if (!isSameHeaderDropSlot(slot, active.slot)) preview(active, slot);
  };
  // A pointer over a pinned section the column isn't from pins it only after a hold, so scrolling
  // the unpinned columns, which takes the pointer to the table's edge, doesn't pin by accident.
  // The hold starts once they can't scroll further that way; until it fills, the column stays at
  // the unpinned edge. The keyboard steps into a pinned section at once.
  const clearHold = (active: HeaderDragSession) => {
    if (!active.hold) return;
    window.clearTimeout(active.hold.timer);
    active.hold = null;
    preview(active, active.slot);
  };
  const holdToPin = (active: HeaderDragSession, slot: HeaderDropSlot, x: number) => {
    const side = slot.region;
    if (side === 'center' || side === getHeaderHomeSlot(active).region) {
      active.armed = null;
      clearHold(active);
      return slot;
    }
    if (active.armed === side) return slot;
    const viewport = connected().getViewport();
    const canScroll = viewport !== null && (side === 'start' ? viewport.scrollLeft > 0 : viewport.scrollLeft < viewport.scrollWidth - viewport.clientWidth - 1);
    if (canScroll) clearHold(active);else if (active.hold?.region !== side) {
      clearHold(active);
      active.hold = {
        region: side,
        timer: window.setTimeout(() => {
          if (session !== active) return;
          active.hold = null;
          active.armed = side;
          followPointer();
          preview(active, active.slot);
        }, HOLD_TO_PIN_MS)
      };
      preview(active, active.slot);
    }
    // The pointer's x inside the unpinned section: its edge, or where the pointer is when a phone's
    // pinned column past the sticky budget scrolls there.
    const {
      endWidth,
      startWidth
    } = getHeaderDragOffsets(active, active.slot, active.geometry);
    const centerStart = (startWidth || HEADER_DROP_EMPTY_BAND) + 1;
    const centerEnd = active.geometry.viewportWidth - (endWidth || HEADER_DROP_EMPTY_BAND) - 1;
    return getHeaderDropSlotAt({
      ...active,
      geometry: active.geometry,
      hitRegion: 'center',
      x: Math.min(Math.max(x, centerStart), centerEnd)
    });
  };

  // Touch fires both pointer and touch moves; one is enough.
  const movePointer = (x: number, y: number) => {
    if (pointer?.x === x && pointer.y === y) return;
    pointer = {
      x,
      y
    };
    followPointer();
  };
  const onPointerMove = (event: PointerEvent) => movePointer(event.clientX, event.clientY);
  const onTouchMove = (event: TouchEvent) => {
    const touch = event.touches[0];
    if (touch) movePointer(touch.clientX, touch.clientY);
  };
  // A scroll moves the columns under the pointer, so the layout is worked out again, once a
  // frame: auto-scroll scrolls far more often than that.
  const onScroll = () => {
    if (scrollFrame) return;
    scrollFrame = requestAnimationFrame(() => {
      scrollFrame = 0;
      const active = session;
      if (!active) return;
      active.geometry = {
        ...active.geometry,
        scrollLeft: connected().getViewport()?.scrollLeft ?? 0
      };
      active.box = viewportBox();
      active.headY = headMiddle(active.activeId);
      preview(active, active.slot, false);
      followPointer();
    });
  };

  // Stops listening to the pointer and the scroll.
  const detach = () => {
    cancelAnimationFrame(scrollFrame);
    scrollFrame = 0;
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('touchmove', onTouchMove);
    deps?.getViewport()?.removeEventListener('scroll', onScroll);
  };
  const start = (activeId: string, via: HeaderDragSession['via']): HeaderDragSession => {
    // A drag still running, such as a keyboard one when a pointer takes another grip, is
    // cancelled, and a drop still settling lands, both at once.
    if (session) end(false);
    flushSettle?.();
    const frozen = freeze(activeId);
    const started: HeaderDragSession = {
      ...frozen,
      armed: null,
      hold: null,
      liftLeft: 0,
      slot: getHeaderHomeSlot(frozen),
      via
    };
    session = started;
    const {
      getViewport,
      isDraggingRef,
      scope,
      tableRef
    } = connected();
    isDraggingRef.current = true;
    tableRef.current?.setAttribute('data-header-drag', scope);
    started.liftLeft = liftColumn(started);
    if (via === 'keyboard') pressX = started.liftLeft;
    preview(started, started.slot);
    // Only a pointer drag follows the pointer; a keyboard drag moves slot by slot.
    if (via === 'pointer') {
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('touchmove', onTouchMove, {
        passive: true
      });
    }
    getViewport()?.addEventListener('scroll', onScroll, {
      passive: true
    });
    return started;
  };
  const commit = (active: HeaderDragSession) => {
    const {
      activeId,
      slot
    } = active;
    const label = getLabel(activeId);
    // First, as a drop home can still move hidden columns beside it.
    if (isSameHeaderDropSlot(slot, getHeaderHomeSlot(active))) {
      announce(getTableSettingsNoMoveAnnouncement(label));
      return;
    }
    const {
      columnOrder: order,
      columnPinning: pinning,
      setColumnOrder,
      setColumnPinning,
      table
    } = connected();
    const next = moveTableColumnLayout({
      columnOrder: order,
      leafColumnIds: table.getAllLeafColumns().map(column => column.id),
      lockedPinning: active.locks,
      move: {
        activeId,
        isSearchActive: false,
        ...getHeaderDropMove(active, slot)
      },
      pinning
    });
    const orderChanged = !sameLayout(order, next.columnOrder);
    const pinningChanged = !sameLayout(pinning.start, next.pinning.start) || !sameLayout(pinning.end, next.pinning.end);
    if (!orderChanged && !pinningChanged) {
      announce(getTableSettingsNoMoveAnnouncement(label));
      return;
    }
    // Rendered at once, so the slides come off in the same frame as the new layout lands.
    flushSync(() => {
      if (orderChanged) setColumnOrder(next.columnOrder);
      if (pinningChanged) setColumnPinning(next.pinning);
    });
    // Counted among the shown columns, as the header shows them.
    const {
      position,
      total
    } = getTableSettingsPosition({
      id: activeId,
      layout: next,
      leafColumnIds: HEADER_DROP_REGIONS.flatMap(region => active.regions[region]),
      region: slot.region
    });
    announce(getTableSettingsMoveAnnouncement({
      destination: regionLabel(slot.region),
      label,
      position,
      total
    }));
  };

  // Takes everything the drag put on the page away.
  const cleanUp = () => {
    lift?.remove();
    lift = null;
    sheet?.remove();
    sheet = null;
    shownSections = '';
    if (!deps) return;
    deps.tableRef.current?.removeAttribute('data-header-drag');
    deps.setSections(null);
  };
  const end = (drop: boolean) => {
    const ended = session;
    if (!ended) return;
    session = null;
    pointer = null;
    connected().isDraggingRef.current = false;
    detach();
    if (ended.hold) window.clearTimeout(ended.hold.timer);

    // The lifted column settles into the gap, or back home, as the columns slide there.
    const {
      gapLeft
    } = preview(ended, drop ? ended.slot : getHeaderHomeSlot(ended));
    if (lift) {
      lift.style.transition = liftTransition(true);
      lift.style.boxShadow = '';
      lift.style.transform = `translate3d(${gapLeft - ended.liftLeft}px, 0, 0) scale(1)`;
    }
    const settle = () => {
      window.clearTimeout(settleTimer);
      settleTimer = 0;
      flushSettle = null;
      if (drop) commit(ended);else announce(`Cancelled. ${getLabel(ended.activeId)} was not moved.`);
      // The new layout has rendered where the slides had moved the columns, so they come off.
      cleanUp();
      // The head remounts when its region changes, so its grip is found again once it renders.
      if (ended.via === 'keyboard' && drop) {
        requestAnimationFrame(() => grips.get(ended.activeId)?.focus());
      }
    };
    flushSettle = settle;
    if (prefersReducedMotion()) settle();else settleTimer = window.setTimeout(settle, MOTION_MS);
  };
  const onGripKeyDown = (columnId: string, event: KeyboardEvent<HTMLButtonElement>) => {
    const isDrop = event.key === ' ' || event.key === 'Enter';
    if (!session) {
      if (!isDrop) return;
      event.preventDefault();
      const started = start(columnId, 'keyboard');
      announce(`Picked up ${getLabel(columnId)}, ${describe(started, started.slot)}. Use the arrow keys to move it, Space or Enter to drop it, or Escape to cancel.`);
      return;
    }
    if (session.via !== 'keyboard' || session.activeId !== columnId) return;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      event.stopPropagation();
      const slot = stepHeaderDropSlot(session.slots, session.slot, event.key === 'ArrowLeft' ? -1 : 1);
      if (!slot) {
        announce(`${getLabel(columnId)} can't move further.`);
        return;
      }
      const {
        gapLeft
      } = preview(session, slot);
      moveLift(gapLeft, true);
      announce(`${getLabel(columnId)}: ${describe(session, slot)}.`);
    } else if (isDrop || event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      end(isDrop);
    }
  };
  return {
    connect: (next: HeaderDragDeps) => {
      const layoutChanged = deps !== null && (next.columnOrder !== deps.columnOrder || next.columnPinning !== deps.columnPinning || next.columnVisibility !== deps.columnVisibility);
      deps = next;
      // Its slots point into the layout it started from, so a drag doesn't outlive it.
      if (layoutChanged) end(false);
    },
    // A grid that unmounts mid-drag or mid-settle leaves nothing behind.
    dispose: () => {
      window.clearTimeout(settleTimer);
      settleTimer = 0;
      flushSettle = null;
      session = null;
      detach();
      cleanUp();
    },
    endPointer: end,
    // Tabbing away from a grip mid-drag cancels the drag, as the keys no longer reach it.
    onGripBlur: (columnId: string) => {
      if (session?.via === 'keyboard' && session.activeId === columnId) end(false);
    },
    onGripKeyDown,
    // Where the pointer took hold, so the lifted column keeps its offset under it.
    onGripPointerDown: (x: number) => {
      pressX = x;
    },
    registerCell: (columnId: string, element: HTMLTableCellElement | null) => {
      if (element) cells.set(columnId, element);else cells.delete(columnId);
    },
    registerGrip: (columnId: string, element: HTMLButtonElement | null) => {
      if (element) grips.set(columnId, element);else grips.delete(columnId);
    },
    // Where the press began, when the sensor's event says; a touch press reports it on pointerdown.
    startPointer: (columnId: string, pressedAt: number | undefined) => {
      if (pressedAt !== undefined) pressX = pressedAt;
      start(columnId, 'pointer');
    }
  };
}
const GRIP_CLASS = cn('-ms-1 flex h-6 w-4 shrink-0 cursor-grab items-center justify-center rounded-sm text-muted-foreground transition-opacity hover:text-foreground active:cursor-grabbing', 'focus-visible:opacity-100 focus-visible:outline-2 focus-visible:outline-ring md:opacity-0 md:group-hover/head:opacity-100 md:in-data-header-drag-lift:opacity-100');

/** The drag handle at the start of a movable head. */
export const HeaderDragGrip = ({
  column
}: {
  column: Column<RowData, unknown>;
}) => {
  const drag = useContext(HeaderDragContext);
  const {
    attributes,
    listeners,
    setNodeRef
  } = useDraggable({
    id: column.id
  });
  return <button {...attributes} {...listeners} aria-label={`Drag to move ${getColumnHeaderLabel(column)}`} className={GRIP_CLASS} onBlur={() => drag?.onGripBlur(column.id)} onKeyDown={event => drag?.onGripKeyDown(column.id, event)} onPointerDown={event_0 => drag?.onGripPointerDown(event_0.clientX)} ref={element => {
    setNodeRef(element);
    drag?.registerGrip(column.id, element);
  }} type="button" data-sentry-component="HeaderDragGrip" data-sentry-source-file="header-drag.tsx">
      <GripVerticalIcon aria-hidden="true" className="size-3.5" data-sentry-element="GripVerticalIcon" data-sentry-source-file="header-drag.tsx" />
    </button>;
};

/** The ref callback that lets header drag measure a column's head cell. */
export const useHeaderDragCell = (columnId: string) => {
  const drag = useContext(HeaderDragContext);
  return (element: HTMLTableCellElement | null) => drag?.registerCell(columnId, element);
};